/**
 * Auth0 configuration
 * Centralized Auth0 settings used across the application
 *
 * @module config/auth
 */

import { DEFAULT_API_BASE, DEFAULT_AUTH0_AUDIENCE } from "./constants";
import { getEnvString } from "../shared/envHelpers";

export type NormalizedAuthConfig = {
  domain: string;
  audience: string;
  apiBaseUrl: string;
  scope: string;
  extensionClientId?: string;
  portalClientId?: string;
};

type AuthEnv = {
  VITE_EXTENSION_AUTH0_DOMAIN?: string | false;
  VITE_EXTENSION_AUTH0_CLIENT_ID?: string | false;
  VITE_PORTAL_AUTH0_CLIENT_ID?: string | false;
  VITE_EXTENSION_AUTH0_AUDIENCE?: string | false;
  VITE_EXTENSION_API_BASE?: string | false;
};

const env = (
  typeof import.meta !== "undefined" ? ((import.meta as ImportMeta & { env?: AuthEnv }).env ?? {}) : {}
) as AuthEnv;

const envDomain = normalize(getEnvString(env.VITE_EXTENSION_AUTH0_DOMAIN));
const envExtensionClientId = normalize(getEnvString(env.VITE_EXTENSION_AUTH0_CLIENT_ID));
const envPortalClientId = normalize(getEnvString(env.VITE_PORTAL_AUTH0_CLIENT_ID));
const envAudience = normalize(getEnvString(env.VITE_EXTENSION_AUTH0_AUDIENCE));
const envApiBase = normalizeBaseUrl(getEnvString(env.VITE_EXTENSION_API_BASE));

const normalized: NormalizedAuthConfig = {
  domain: envDomain ?? "lotlingo.us.auth0.com",
  audience: envAudience ?? DEFAULT_AUTH0_AUDIENCE.replace(/\/$/, ""),
  apiBaseUrl: envApiBase ?? normalizeBaseUrl(DEFAULT_API_BASE) ?? DEFAULT_API_BASE,
  scope:
    normalize(getEnvString((env as any).VITE_EXTENSION_AUTH0_SCOPE)) ??
    "openid profile email extension.scrapes.read extension.scrapes.write offline_access",
  extensionClientId: envExtensionClientId,
  portalClientId: envPortalClientId ?? envExtensionClientId,
};

/**
 * Auth0 configuration object
 * All Auth0-related settings in one place
 */
export const AUTH0_CONFIG = {
  /**
   * Auth0 domain
   * Can be overridden via VITE_EXTENSION_AUTH0_DOMAIN environment variable
   */
  DOMAIN: normalized.domain,

  /**
   * Auth0 client ID
   * Must be provided via VITE_PORTAL_AUTH0_CLIENT_ID or VITE_EXTENSION_AUTH0_CLIENT_ID
   */
  CLIENT_ID: normalized.portalClientId ?? "",

  /**
   * Auth0 audience
   * Should match the API base URL
   */
  AUDIENCE: normalized.audience,

  /**
   * Auth0 OAuth scopes
   * Defines what permissions the application requests
   */
  SCOPE: normalized.scope,
} as const;

export function getAuth0Domain(): string {
  return normalized.domain;
}

export function getAuth0Audience(): string {
  return normalized.audience;
}

export function getApiBaseUrl(): string {
  return normalized.apiBaseUrl;
}

export function getAuth0Scope(): string {
  return normalized.scope;
}

export function getExtensionClientId(): string | undefined {
  return normalized.extensionClientId;
}

export function getPortalClientId(): string | undefined {
  return normalized.portalClientId;
}

export function getNormalizedAuthConfig(): NormalizedAuthConfig {
  return { ...normalized };
}

/**
 * Helper function to validate Auth0 client ID is configured
 * Throws error if client ID is missing in production builds
 */
export function validateAuth0Config(): void {
  if (!AUTH0_CONFIG.CLIENT_ID) {
    const isDev = typeof import.meta !== "undefined" && import.meta.env?.DEV;
    if (!isDev) {
      throw new Error(
        "Auth0 client ID is not set. " +
          "Provide VITE_PORTAL_AUTH0_CLIENT_ID or VITE_EXTENSION_AUTH0_CLIENT_ID before building the application."
      );
    }
  }
}

function normalize(value: string | undefined | null): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
}

function normalizeBaseUrl(value: string | undefined | null): string | undefined {
  const normalizedValue = normalize(value);
  if (!normalizedValue) {
    return undefined;
  }
  return normalizedValue.replace(/\/$/, "");
}
